import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { claseEntradaMenu, type GrupoMenu } from './navegacion';
import { usarMenuFlotante } from './usarMenuFlotante';

/**
 * Navegación y cuenta en un solo menú, para pantallas donde la barra no cabe en una fila: con doce
 * entradas apiladas el encabezado fijo se lleva un cuarto de la pantalla todo el turno. El botón
 * muestra la sección activa, para que orientarse no exija abrirlo.
 */
export function MenuPrincipal({
  grupos,
  seccionActiva,
  children,
}: {
  grupos: GrupoMenu[];
  seccionActiva: string;
  /**
   * Bloque de cuenta, al final del menú. Recibe `cerrar` para que lo que navegue cierre el
   * menú y lo que solo abra un diálogo lo deje en pie.
   */
  children: (cerrar: () => void) => ReactNode;
}) {
  const [abierto, setAbierto] = useState(false);
  const cerrar = useCallback(() => setAbierto(false), []);
  const { contenedor, disparador } = usarMenuFlotante(abierto, cerrar);

  return (
    <div ref={contenedor} className="relative">
      <button
        ref={disparador}
        onClick={() => setAbierto((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={abierto}
        data-e2e="menu-principal"
        className="rounded-control hover:bg-surface min-h-(--alto-control) flex items-center gap-2 px-3 py-2 text-contenido font-bold"
      >
        <span aria-hidden>☰</span>
        {seccionActiva}
      </button>

      {abierto && (
        <div
          role="menu"
          data-e2e="menu-abierto"
          className="rounded-card border-border bg-card absolute right-0 z-40 mt-1 max-h-[80vh] w-64 overflow-y-auto border p-2 shadow-lg"
        >
          {grupos.map((grupo) => (
            <div key={grupo.titulo} className="mb-2" data-e2e="menu-grupo">
              <p className="text-muted px-2 py-1 text-etiqueta font-bold uppercase tracking-wide">{grupo.titulo}</p>
              {grupo.entradas.map((entrada) =>
                entrada.ruta ? (
                  <NavLink
                    key={entrada.texto}
                    to={entrada.ruta}
                    end={entrada.fin}
                    role="menuitem"
                    data-e2e={entrada.e2e}
                    onClick={cerrar}
                    className={({ isActive }) => claseEntradaMenu(isActive)}
                  >
                    {entrada.texto}
                  </NavLink>
                ) : (
                  <button
                    key={entrada.texto}
                    role="menuitem"
                    data-e2e={entrada.e2e}
                    onClick={() => {
                      entrada.alElegir?.();
                      cerrar();
                    }}
                    className={claseEntradaMenu(entrada.activa === true)}
                  >
                    {entrada.texto}
                  </button>
                )
              )}
            </div>
          ))}

          <div className="border-border text-muted flex flex-col gap-1 border-t pt-2 text-contenido">{children(cerrar)}</div>
        </div>
      )}
    </div>
  );
}
